export interface FlightSchoolModule {
  id: string;
  title: string;
  subtitle: string;
  type: "cognate";
  isComingSoon?: boolean;
}

export interface FlightSchoolCompletionContext {
  modules?: FlightSchoolModule[];
  isLastLessonInModule?: boolean;
  chapterCompleteTitle?: string;
}

export type FlightSchoolModuleStatus =
  | "completed"
  | "current"
  | "locked"
  | "coming_soon"
  | "skipped";

export function isAvailableFlightSchoolModule(
  module: FlightSchoolModule,
): boolean {
  return !("isComingSoon" in module && module.isComingSoon);
}

export function isLastAvailableFlightSchoolModule(
  moduleId: string,
  modules: FlightSchoolModule[],
): boolean {
  const available = modules.filter(isAvailableFlightSchoolModule);
  if (available.length === 0) return false;

  return available[available.length - 1].id === moduleId;
}

export function getFlightSchoolProgressionFloorIndex(
  sourceModules: FlightSchoolModule[],
  currentModuleOverrideId: string | null,
): number {
  if (!currentModuleOverrideId) return 0;
  const overrideIndex = sourceModules.findIndex(
    (module) =>
      module.id === currentModuleOverrideId &&
      isAvailableFlightSchoolModule(module),
  );
  return overrideIndex >= 0 ? overrideIndex : 0;
}

export function getFirstIncompleteFlightSchoolModuleId(
  sourceModules: FlightSchoolModule[],
  completedModules: string[],
  currentModuleOverrideId: string | null = null,
): string | null {
  const completedSet = new Set(completedModules);
  const floorIndex = getFlightSchoolProgressionFloorIndex(
    sourceModules,
    currentModuleOverrideId,
  );
  return (
    sourceModules
      .slice(floorIndex)
      .find(
        (module) =>
          isAvailableFlightSchoolModule(module) && !completedSet.has(module.id),
      )?.id ?? null
  );
}

export function hasCompletedFlightSchoolProgression(
  sourceModules: FlightSchoolModule[],
  completedModules: string[],
  currentModuleOverrideId: string | null = null,
): boolean {
  const completedSet = new Set(completedModules);
  const floorIndex = getFlightSchoolProgressionFloorIndex(
    sourceModules,
    currentModuleOverrideId,
  );
  return sourceModules
    .slice(floorIndex)
    .filter(isAvailableFlightSchoolModule)
    .every((module) => completedSet.has(module.id));
}

export function getAvailableFlightSchoolModuleCount(
  sourceModules: FlightSchoolModule[],
): number {
  return sourceModules.filter(isAvailableFlightSchoolModule).length;
}

export function getCurrentFlightSchoolModuleId(
  sourceModules: FlightSchoolModule[],
  completedModules: string[],
  currentModuleOverrideId: string | null = null,
): string {
  const firstIncomplete = getFirstIncompleteFlightSchoolModuleId(
    sourceModules,
    completedModules,
    currentModuleOverrideId,
  );
  return (
    firstIncomplete ??
    sourceModules.find(isAvailableFlightSchoolModule)?.id ??
    "cognates-1"
  );
}

export function deriveFlightSchoolModuleStatuses(
  sourceModules: FlightSchoolModule[],
  completedModules: string[],
  currentModuleOverrideId: string | null = null,
): Record<string, FlightSchoolModuleStatus> {
  const statuses: Record<string, FlightSchoolModuleStatus> = {};
  const currentModule = getFirstIncompleteFlightSchoolModuleId(
    sourceModules,
    completedModules,
    currentModuleOverrideId,
  );
  const currentIndex = currentModule
    ? sourceModules.findIndex((m) => m.id === currentModule)
    : -1;
  const progressionFloorIndex = getFlightSchoolProgressionFloorIndex(
    sourceModules,
    currentModuleOverrideId,
  );

  for (let index = 0; index < sourceModules.length; index += 1) {
    const module = sourceModules[index];
    if (!isAvailableFlightSchoolModule(module)) {
      statuses[module.id] = "coming_soon";
    } else if (completedModules.includes(module.id)) {
      statuses[module.id] = "completed";
    } else if (currentModule === module.id) {
      statuses[module.id] = "current";
    } else if (
      index < progressionFloorIndex ||
      (currentIndex >= 0 && index < currentIndex)
    ) {
      statuses[module.id] = "skipped";
    } else {
      const previousModules = sourceModules.slice(
        progressionFloorIndex,
        index,
      );
      const allPreviousCompleted = previousModules.every(
        (m) =>
          !isAvailableFlightSchoolModule(m) || completedModules.includes(m.id),
      );
      statuses[module.id] = allPreviousCompleted ? "current" : "locked";
    }
  }

  return statuses;
}

function getFlightSchoolCompletionContext(
  defaultModules: FlightSchoolModule[],
  context: FlightSchoolModule[] | FlightSchoolCompletionContext = {},
): Required<FlightSchoolCompletionContext> {
  if (Array.isArray(context)) {
    return {
      modules: context,
      isLastLessonInModule: true,
      chapterCompleteTitle: "Chapter Complete!",
    };
  }

  return {
    modules: context.modules ?? defaultModules,
    isLastLessonInModule: context.isLastLessonInModule ?? true,
    chapterCompleteTitle: context.chapterCompleteTitle ?? "Chapter Complete!",
  };
}

export function resolveFlightSchoolCompleteCelebration(
  moduleId: string,
  defaultModules: FlightSchoolModule[],
  context?: FlightSchoolModule[] | FlightSchoolCompletionContext,
): boolean {
  const { modules, isLastLessonInModule } = getFlightSchoolCompletionContext(
    defaultModules,
    context,
  );

  return (
    isLastLessonInModule &&
    isLastAvailableFlightSchoolModule(moduleId, modules)
  );
}

export function resolveFlightSchoolModuleCompleteTitle(
  moduleId: string,
  defaultModules: FlightSchoolModule[],
  context?: FlightSchoolModule[] | FlightSchoolCompletionContext,
): string {
  const { chapterCompleteTitle } = getFlightSchoolCompletionContext(
    defaultModules,
    context,
  );

  return resolveFlightSchoolCompleteCelebration(
    moduleId,
    defaultModules,
    context,
  )
    ? "Flight School Complete"
    : chapterCompleteTitle;
}
